import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Loader2, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { faqAPI } from '../../services/api';

const FAQManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'category' | 'item', catIdx, itemIdx? }

  useEffect(() => {
    fetchFAQ();
  }, []);

  const fetchFAQ = async () => {
    try {
      const res = await faqAPI.getAll();
      setCategories(res.data);
      // Expand all by default
      const expanded = {};
      res.data.forEach((_, i) => { expanded[i] = true; });
      setExpandedCats(expanded);
    } catch (e) {
      console.error('Error fetching FAQ:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = categories.map(cat => ({
        category: cat.category,
        items: cat.items.map(item => ({
          question: item.question,
          answer: item.answer,
        })),
      }));
      const res = await faqAPI.update(payload);
      setCategories(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('Error saving FAQ:', e);
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    const newIdx = categories.length;
    setCategories([...categories, { id: '', category: 'New Category', items: [{ question: '', answer: '' }] }]);
    setExpandedCats({ ...expandedCats, [newIdx]: true });
  };

  const updateCategoryName = (catIdx, name) => {
    setCategories(categories.map((c, i) => i === catIdx ? { ...c, category: name } : c));
  };

  const removeCategory = () => {
    if (!deleteTarget || deleteTarget.type !== 'category') return;
    setCategories(categories.filter((_, i) => i !== deleteTarget.catIdx));
    setDeleteTarget(null);
  };

  const addItem = (catIdx) => {
    setCategories(categories.map((c, i) =>
      i === catIdx ? { ...c, items: [...c.items, { question: '', answer: '' }] } : c
    ));
  };

  const updateItem = (catIdx, itemIdx, field, value) => {
    setCategories(categories.map((c, ci) =>
      ci === catIdx ? {
        ...c,
        items: c.items.map((item, ii) =>
          ii === itemIdx ? { ...item, [field]: value } : item
        )
      } : c
    ));
  };

  const removeItem = () => {
    if (!deleteTarget || deleteTarget.type !== 'item') return;
    const { catIdx, itemIdx } = deleteTarget;
    setCategories(categories.map((c, ci) =>
      ci === catIdx ? { ...c, items: c.items.filter((_, ii) => ii !== itemIdx) } : c
    ));
    setDeleteTarget(null);
  };

  const moveCategoryUp = (idx) => {
    if (idx === 0) return;
    const newCats = [...categories];
    [newCats[idx - 1], newCats[idx]] = [newCats[idx], newCats[idx - 1]];
    setCategories(newCats);
  };

  const moveCategoryDown = (idx) => {
    if (idx === categories.length - 1) return;
    const newCats = [...categories];
    [newCats[idx], newCats[idx + 1]] = [newCats[idx + 1], newCats[idx]];
    setCategories(newCats);
  };

  const toggleExpand = (idx) => {
    setExpandedCats({ ...expandedCats, [idx]: !expandedCats[idx] });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">FAQ Manager</h1>
          <p className="text-zinc-400">Manage frequently asked questions shown on the FAQ page</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={addCategory}
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
            ) : saved ? (
              <><Save className="h-4 w-4 mr-2" />Saved!</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Save All</>
            )}
          </Button>
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-12 text-center">
          <p className="text-zinc-400 mb-4">No FAQ categories yet</p>
          <Button onClick={addCategory} className="bg-amber-500 hover:bg-amber-600 text-black">
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Category
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((cat, catIdx) => (
            <div key={catIdx} className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              {/* Category Header */}
              <div className="p-4 flex items-center gap-3 border-b border-zinc-800">
                <div className="flex flex-col">
                  <button
                    onClick={() => moveCategoryUp(catIdx)}
                    className="text-zinc-500 hover:text-white disabled:opacity-30"
                    disabled={catIdx === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => moveCategoryDown(catIdx)}
                    className="text-zinc-500 hover:text-white disabled:opacity-30"
                    disabled={catIdx === categories.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>

                <Input
                  value={cat.category}
                  onChange={(e) => updateCategoryName(catIdx, e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white font-semibold"
                  placeholder="Category name"
                />

                <span className="text-zinc-500 text-sm whitespace-nowrap">
                  {cat.items.length} {cat.items.length === 1 ? 'item' : 'items'}
                </span>

                <button
                  onClick={() => toggleExpand(catIdx)}
                  className="text-zinc-400 hover:text-white p-1"
                >
                  <ChevronDown className={`h-5 w-5 transition-transform ${expandedCats[catIdx] ? 'rotate-180' : ''}`} />
                </button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => setDeleteTarget({ type: 'category', catIdx })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Items */}
              {expandedCats[catIdx] && (
                <div className="p-4 space-y-4">
                  {cat.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="bg-zinc-800 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-3">
                          <div>
                            <Label className="text-zinc-400 text-xs">Question</Label>
                            <Input
                              value={item.question}
                              onChange={(e) => updateItem(catIdx, itemIdx, 'question', e.target.value)}
                              className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                              placeholder="Enter question..."
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Answer</Label>
                            <Textarea
                              value={item.answer}
                              onChange={(e) => updateItem(catIdx, itemIdx, 'answer', e.target.value)}
                              className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                              placeholder="Enter answer..."
                              rows={3}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-400 hover:text-red-300 mt-5"
                          onClick={() => setDeleteTarget({ type: 'item', catIdx, itemIdx })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addItem(catIdx)}
                    className="border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800 w-full border-dashed"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Delete {deleteTarget?.type === 'category' ? 'Category' : 'Question'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {deleteTarget?.type === 'category'
                ? 'This will delete the entire category and all its questions. This action cannot be undone.'
                : 'Are you sure you want to delete this question?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteTarget?.type === 'category' ? removeCategory : removeItem}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FAQManager;
